import moment from 'moment-timezone';
import { sendEncryptedRequest } from './cipher';
import { CardInput } from './cardDetails';

/** Functions to handle the handling of payment processing */

//Order Details expected by the ProcessPayment function
export interface OrderDetails
{
    timestamp: string,
    currencyCode: number,
    lineItems: LineItemOrderDetails[],
    totalCost: string,
    paymentMethod?: CardInput,
    cartId?: string
}

export interface LineItemOrderDetails
{
    name: string,
    dbId: number,
    price: 
    {
        qty: number,
        unitPrice: string,
        totalPrice: string
    },
    data: any
}

export interface ProcessPaymentReturn
{
    status: string,
    reason?: string,
    payment_id?: string,
    pollState?: Promise<any>
}

/**
 * Process an order made by a user with the provided credentials.
 * @param order The order to be Processed
 * @param pubKey The public key used to encrypt the encryption key with
 * @param authToken the auth token for the logged in user (if logged in)
 * @returns A ProcessPaymentReturn with a status, reason, and payment_id for the initial request.
 * Also, a pollState promise to monitor the processing of the payment, which will eventually return a response
 * with the final result of the payment processing.
 */
//TODO: It may be cleaner to just provide the entire GACntxtIface here as opposed to splitting out pubKey and authToken.
export const ProcessPayment = async (order: OrderDetails, pubKey: string, authToken?: string): Promise<ProcessPaymentReturn> =>
{
    if(order.paymentMethod === undefined)
    {
        //Can't process due to no payment provided
        return({status: 'failed', reason: 'no card provided'});

    }
    
    const auth = authToken ? `Bearer ${authToken}` : 'Basic YnV6ejpmcjBudGllcg==';

    const requestOptions =
    {
        method: 'POST',
        headers: { 'Authorization': auth},
    }

    const encryptedPayload = 
    {
        totalCost: order.totalCost,
        paymentMethod: order.paymentMethod,
    }

    const unEncryptedPayload =
    {
        timestamp: order.timestamp,
        currencyCode: order.currencyCode,
        lineItems: order.lineItems,
        cartId: order.cartId
    }

    //Send Encrypted Message
    return await sendEncryptedRequest(encryptedPayload, unEncryptedPayload, new URL(process.env.REACT_APP_REFLEBULA + '/a1/tga/payment/process'), requestOptions, pubKey)
        .then(response =>
        {
            const paymentID = response.payment_id;

            if(response.ackResponse.result !== "success")
            {
                return({
                    status: response.ackResponse ? response.ackResponse.result : response.initialResponse.status,
                    reason: response.ackResponse ? response.ackResponse.reason : response.ackResponse.reason,
                    failureSource: response.ackResponse ? 'ackResponse' : 'initialResponse'
                })
            }
            if(paymentID === undefined)
            {
                return({
                    status: "failure",
                    reason: "No Payment ID provided",
                    failureSource: "initialResponse"
                })
            }

            //Start the polling, but don't wait here for it to finish.
            const pollState = pollForStatus(paymentID, authToken);

            return({
                status: "success",
                payment_id: paymentID,
                pollState: pollState
            })
        });
    
}

interface pollForStatusResult
{
    status: string,
    paymentId?: string,
    orderId?: string
}

/**
 * Poll for the status of the indicated payment.
 * @param paymentID The payment ID to monitor
 * @param authToken The authtoken of the user, if any
 * @returns structure where 'status' identifies the result (success, failure, or failure_timeout) 
 */
const pollForStatus = async (paymentID: string, authToken?: string): Promise<pollForStatusResult> => 
{
    const timeoutSeconds = Number(process.env.REACT_APP_PAYMENT_PROCESSING_POLLING_TIMEOUT) || 300;
    const endTime = moment().add(timeoutSeconds, 'seconds');

    while (moment().isBefore(endTime)) {
        const response = await requestStatus(paymentID, authToken);
        
        if (response.status === 'pending') {
            await new Promise(resolve => setTimeout(resolve, 1000));
        }
        else
        {
            if(response.status === 'success')
            {
                return({
                    status: 'success',
                    paymentId: response.payment_id,
                    orderId: response.order_id
                });
            }
            else
            {
                return({
                    status: 'failure'
                })
            }
        }

        
    }

    // If loop finishes without returning, timeout
    return {
        status: 'failure_timeout'
    };
};


export const singularPollForStatus = async (paymentID: string, authToken?: string): Promise<pollForStatusResult> => 
{
    const response = await requestStatus(paymentID, authToken);
    
    if(response.status === 'success')
    {
        return({
            status: 'success',
            paymentId: response.payment_id,
            orderId: response.order_id
        });
    }
    else
    {
        return({
            status: response.status === 'pending' ? 'pending' : 'failure'
        })
    }
}

/**
 * Requests the current status of a given payment
 * TODO: This function is under construction, and is mostly a placeholder for now.
 * @param paymentID the payment ID to request the status of
 * @param authToken the auth token of the user (is this necessary?)
 * @returns The JSON object of the response
 */
export const requestStatus = async (paymentID:string, authToken?: string): Promise<any> =>
{

    const auth = authToken ? `Bearer ${authToken}` : 'Basic YnV6ejpmcjBudGllcg==';

    const requestOptions =
    {
        method: 'GET',
        headers: { 'Authorization': auth},
    }

    const url = new URL(process.env.REACT_APP_REFLEBULA + '/a1/tga/payment/status');
    url.searchParams.append('payment_id', paymentID);

    return fetch(url, requestOptions)
        .then(response => response.json());
}

export const getPublicKey = async (user_id?: number): Promise<string> =>
{
    const uId = user_id ? user_id : 16;

    return fetch(process.env.REACT_APP_REFLEBULA + "/a1/tga/user/" + uId, { headers: { Authorization: 'Basic YnV6ejpmcjBudGllcg==' }, })
        .then((response) => response.json())
        .then((response) => {
            return (response.pub_key);
        })

}
